import { Link } from "react-router-dom";
import { categories, contactChannels, principles, siteInfo, stats, testimonials } from "@/mocks/site";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import LeadForm from "./components/LeadForm";

const positioning = [
  {
    icon: "ri-compass-3-line",
    title: "面向补课型读者",
    desc: "不做即时快讯抢发，而是把已经发生的事整理成能看懂的结构，帮错过热点的读者快速补齐上下文。",
  },
  {
    icon: "ri-layout-grid-line",
    title: "以栏目为单位",
    desc: "六个常设栏目各自负责一类内容，年度盘点、TOP榜、名场面、人物志、经典案例与合集整理互不重叠。",
  },
  {
    icon: "ri-scales-3-line",
    title: "事实与观点分开",
    desc: "时间线与公开回应作为事实单独呈现，分析与观察单独成段，读者可以只看事实不看我们的判断。",
  },
];

export default function About() {
  return (
    <div className="min-h-screen bg-background-50">
      <Seo
        title="关于瓜田 | 瓜田黑料内容原则与联系"
        description="了解瓜田黑料的品牌介绍、网站定位、发展理念与内容原则。我们只做公开信息整理与复盘，倡导理性吃瓜，并欢迎通过线索征集渠道提供后续进展。"
        keywords="关于瓜田,瓜田黑料介绍,内容原则,理性吃瓜"
        path="/about"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "AboutPage",
              name: "关于瓜田",
              description: "瓜田黑料的品牌介绍、网站定位与内容原则",
              inLanguage: "zh-CN",
              url: "https://jiemia.cn/about",
            },
            {
              "@type": "Organization",
              name: "瓜田黑料",
              url: "https://jiemia.cn/",
              email: "hello@guatianheiliao.com",
              description: "娱乐话题年度盘点、TOP榜与名场面合集内容门户",
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://jiemia.cn/" },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "关于瓜田",
                  item: "https://jiemia.cn/about",
                },
              ],
            },
          ],
        }}
      />
      <SiteHeader />

      <main className="pt-16 md:pt-20">
        <div className="w-full px-4 md:px-10 pt-8 md:pt-10">
          <Breadcrumb items={[{ name: "首页", path: "/" }, { name: "关于瓜田" }]} />
        </div>

        <section className="w-full px-4 md:px-10 pt-6 pb-12">
          <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-10">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-[0.18em] text-primary-600 uppercase">
              <span className="w-4 h-px bg-primary-400" />
              About Guatian
            </span>
            <h1 className="mt-3 font-heading font-black text-2xl md:text-4xl text-foreground-950">
              关于瓜田黑料
            </h1>
            <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-600">
              {siteInfo.intro}
            </p>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="border-l-2 border-primary-400 pl-4">
                  <p className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                    {stat.value}
                    <span className="text-sm font-normal text-foreground-500 ml-1">{stat.suffix}</span>
                  </p>
                  <p className="mt-1 text-xs md:text-sm text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-10 pb-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=abstract%20warm%20editorial%20desk%20with%20stacked%20magazines%2C%20notebook%2C%20amber%20desk%20lamp%20glow%20and%20cream%20paper%20textures%2C%20cozy%20minimal%20still%20life%2C%20no%20readable%20text%2C%20no%20people&width=1200&height=900&seq=gt-about-desk&orientation=landscape"
                alt="瓜田黑料编辑部工作场景抽象示意"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <h2 className="font-heading font-black text-2xl md:text-3xl text-foreground-950">
                我们想做的事
              </h2>
              <p className="mt-4 text-sm md:text-base leading-loose text-foreground-700">
                娱乐话题的特点是爆发快、消耗也快。热搜每隔几天换一轮，很多人只是刷到了碎片，却拼不出完整的故事。瓜田黑料想做的是把碎片重新拼起来：什么时候发生、当事方怎么说、讨论是怎么起来的、后来又怎么样了。
              </p>
              <p className="mt-4 text-sm md:text-base leading-loose text-foreground-700">
                我们不做人身攻击，也不追求情绪化的表达。整理清楚、标注来源、保留时间线，是这份内容能不能被信任的前提。我们希望读者关掉页面之后，留下的是更清楚的判断，而不是更上头的情绪。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/rankings"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-trophy-line" />
                  看年度 TOP 榜
                </Link>
                <Link
                  to="/faq"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-question-line" />
                  常见问题
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-10 py-14 bg-background-100">
          <SectionHeading
            eyebrow="Positioning"
            title="网站定位 · 内容组织形式"
            desc="每一类内容各归各位，读者可以按需取用。"
          />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {positioning.map((item) => (
              <div key={item.title} className="rounded-lg bg-background-50 border border-background-200 p-6">
                <span className="inline-flex w-11 h-11 items-center justify-center rounded-full bg-primary-50 text-2xl text-primary-600">
                  <i className={item.icon} />
                </span>
                <h3 className="mt-4 font-heading font-bold text-base text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((cat) => (
              <div
                key={cat.slug}
                className="rounded-lg bg-background-50 border border-background-200 p-4 text-center"
              >
                <span className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-accent-100 text-xl text-accent-700">
                  <i className={cat.icon} />
                </span>
                <p className="mt-3 text-sm font-medium text-foreground-950">{cat.name}</p>
                <p className="mt-1 text-xs text-foreground-500">{cat.count} 篇</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-10 py-14 bg-background-50">
          <SectionHeading
            eyebrow="Principles"
            title="发展理念与内容原则"
            desc="这四条是我们在每一篇内容里都会遵守的底线。"
          />
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {principles.map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-4 rounded-lg bg-background-100 border border-background-200 p-6"
              >
                <span className="flex-shrink-0 inline-flex w-11 h-11 items-center justify-center rounded-full bg-secondary-100 text-2xl text-secondary-700">
                  <i className={item.icon} />
                </span>
                <div>
                  <h3 className="font-heading font-bold text-base text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-10 py-14 bg-background-100">
          <SectionHeading
            eyebrow="Readers"
            title="用户口碑"
            desc="来自长期读者的真实反馈，也是我们持续更新下去的动力。"
          />
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            {testimonials.map((item) => (
              <figure
                key={item.name}
                className="rounded-lg bg-background-50 border border-background-200 p-6 flex flex-col"
              >
                <span className="text-accent-500 text-lg">
                  <i className="ri-double-quotes-l" />
                </span>
                <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700 flex-1">
                  {item.content}
                </blockquote>
                <figcaption className="mt-4 pt-4 border-t border-background-200 flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-bold">
                    {item.name.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-foreground-950">{item.name}</span>
                    <span className="block text-xs text-foreground-500">{item.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-10 py-14 bg-background-50">
          <SectionHeading
            eyebrow="Contact"
            title="联系方式与线索征集"
            desc="内容问题、后续进展或商务合作，都可以直接联系我们。"
          />
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-8">
            <div className="space-y-4">
              {contactChannels.map((channel) => (
                <div
                  key={channel.label}
                  className="flex items-start gap-3 rounded-lg bg-background-100 border border-background-200 p-5"
                >
                  <span className="flex-shrink-0 inline-flex w-10 h-10 items-center justify-center rounded-full bg-primary-50 text-xl text-primary-600">
                    <i className={channel.icon} />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">{channel.label}</span>
                    <span className="block mt-0.5 text-sm font-medium text-foreground-950">
                      {channel.value}
                    </span>
                  </span>
                </div>
              ))}
              <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-5">
                <p className="text-xs leading-relaxed text-secondary-800">
                  <i className="ri-time-line mr-1" />
                  非工作时间的提交会在下一个工作日处理，急事建议直接发送邮件。
                </p>
              </div>
            </div>
            <LeadForm />
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}